import { create } from 'zustand';

declare global {
  interface Window {
    vad: any;
    ort: any;
  }
}

interface VadState {
  vadInitialized: boolean;
  vadInstance: any | null;
  isLoading: boolean;
  error: string | null;
  
  initializeVad: () => Promise<boolean>;
  getVadInstance: () => any | null;
  cleanup: () => void;
}

// VAD Configuration
const VAD_CONFIG = {
  silenceTimeout: 500,
  minSpeechFrames: 5,
  positiveSpeechThreshold: 0.6,
  negativeSpeechThreshold: 0.35,
  maxSessionDuration: 30000,
  frameSamples: 512,
  model: 'v5' as const
};

// Singleton promise to ensure only one load attempt
let loadPromise: Promise<boolean> | null = null;

export const useVadStore = create<VadState>((set, get) => ({
  vadInitialized: false,
  vadInstance: null,
  isLoading: false,
  error: null,

  initializeVad: async () => {
    const state = get();
    
    // Already initialized
    if (state.vadInitialized && state.vadInstance) {
      return true;
    }
    
    // Already loading - return the existing promise
    if (loadPromise) {
      return loadPromise;
    }
    
    // Start loading
    loadPromise = (async () => {
      set({ isLoading: true, error: null });
      
      try {
        // Load scripts if not already loaded
        const scriptsLoaded = await loadVadScripts();
        if (!scriptsLoaded) {
          throw new Error('Failed to load VAD scripts');
        }
        
        // Initialize VAD instance
        const vadInstance = await createVadInstance();
        if (!vadInstance) {
          throw new Error('Failed to create VAD instance');
        }
        
        set({ 
          vadInitialized: true, 
          vadInstance,
          isLoading: false,
          error: null
        });
        
        return true;
      } catch (error: any) {
        set({ 
          vadInitialized: false,
          vadInstance: null,
          isLoading: false,
          error: error.message || 'Unknown error'
        });
        return false;
      } finally {
        loadPromise = null;
      }
    })();
    
    return loadPromise;
  },

  getVadInstance: () => {
    return get().vadInstance;
  },

  cleanup: () => {
    const { vadInstance } = get();
    if (vadInstance) {
      try {
        vadInstance.destroy?.();
      } catch (error) {
        console.warn('Error destroying VAD instance:', error);
      }
    }
    set({ vadInstance: null, vadInitialized: false });
  }
}));

// Helper function to load VAD scripts
async function loadVadScripts(): Promise<boolean> {
  // Check if already loaded
  if (typeof window !== 'undefined' && 
      typeof window.vad !== 'undefined' && 
      typeof window.ort !== 'undefined') {
    console.log('VAD scripts already loaded');
    return true;
  }

  return new Promise((resolve) => {
    let onnxLoaded = false;
    let vadLoaded = false;
    
    const checkBothLoaded = async () => {
      if (onnxLoaded && vadLoaded) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (typeof window.ort !== 'undefined' && window.ort.InferenceSession) {
          console.log('VAD scripts fully initialized');
          resolve(true);
        } else {
          console.error('ONNX Runtime not properly initialized');
          resolve(false);
        }
      }
    };

    // Load ONNX Runtime
    const onnxScript = document.createElement('script');
    onnxScript.src = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.14.0/dist/ort.js';
    onnxScript.async = true;
    onnxScript.onload = async () => {
      console.log('ONNX Runtime script loaded');
      let attempts = 0;
      const waitForOrt = () => {
        attempts++;
        if (typeof window.ort !== 'undefined' && window.ort.InferenceSession) {
          onnxLoaded = true;
          checkBothLoaded();
        } else if (attempts < 50) {
          setTimeout(waitForOrt, 100);
        } else {
          resolve(false);
        }
      };
      waitForOrt();
    };
    onnxScript.onerror = () => resolve(false);

    // Load VAD
    const vadScript = document.createElement('script');
    vadScript.src = 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.22/dist/bundle.min.js';
    vadScript.async = true;
    vadScript.onload = () => {
      console.log('VAD library loaded');
      vadLoaded = true;
      checkBothLoaded();
    };
    vadScript.onerror = () => resolve(false);

    // Add scripts to head if not already there
    if (!document.querySelector('script[src*="onnxruntime-web"]')) {
      document.head.appendChild(onnxScript);
    } else {
      onnxLoaded = true;
    }
    
    setTimeout(() => {
      if (!document.querySelector('script[src*="vad-web"]')) {
        document.head.appendChild(vadScript);
      } else {
        vadLoaded = true;
      }
      checkBothLoaded();
    }, 200);
  });
}

// Helper function to create VAD instance
async function createVadInstance(): Promise<any> {
  if (typeof window.vad === 'undefined') {
    throw new Error('VAD library not loaded');
  }
  
  // We'll create the actual VAD instance when needed by the hook
  // This just validates that we can create one
  return true; // Return true to indicate readiness
}